// run-command-pending-question-hint.ts
//
// 06g R6a (QA NST): when `run` stops on `interview_unanswered`, the operator's
// next action is one exact command. For every pending question this ticket is
// waiting on, print `avatar autopilot answer --mission <id> --question <qid>
// --text <option-id>` and the `id: label` lines of its options (label ≤ 80
// chars, no secrets — labels are worker text already redacted at the store).
// Other tickets' questions and answered ones are not listed (same filter the
// gate itself uses: interview-required-answers-gate.ts). 06k C13 (F-55): the
// worker's recommended option (journal `decision_question_asked`, via the
// decision ledger — interview.json never carries it) is marked `(khuyến nghị)`.
import { readDecisionLedger } from "./decision-ledger-projection.js";
import { isBootstrapTicketKind } from "./feature-dispatch-gate-guard.js";
import {
  pendingOwnNeedsDecisionQuestionIds,
  requireAnsweredQuestionIds,
} from "./interview-required-answers-gate.js";
import { type InterviewQuestion, readInterview } from "./interview-store.js";
import type { TicketKind } from "./schemas/ticket-schema.js";

const LABEL_MAX = 80;

function capLabel(label: string): string {
  const points = Array.from(label);
  return points.length <= LABEL_MAX ? label : `${points.slice(0, LABEL_MAX - 1).join("")}…`;
}

function linesForQuestion(
  question: InterviewQuestion,
  missionId: string,
  recommended: string | undefined,
): string[] {
  const head = `avatar autopilot answer --mission ${missionId} --question ${question.id}`;
  if (question.type === "text" || question.options === undefined) {
    return [`${head} --text "<câu trả lời>"`];
  }
  const ids = question.option_ids ?? question.options;
  return [
    `${head} --text <option-id>`,
    ...question.options.map((label, i) => {
      const id = ids[i] ?? label;
      return `  ${id}: ${capLabel(label)}${id === recommended ? " (khuyến nghị)" : ""}`;
    }),
  ];
}

/** Lines to print under the gate-block line; `[]` for any other block code or when nothing is pending. */
export async function answerCommandLines(
  controlRoot: string,
  missionId: string,
  gateBlocked: { ticketId: string; code: string; ticketKind?: TicketKind } | undefined,
): Promise<string[]> {
  if (gateBlocked?.code !== "interview_unanswered") return [];
  // 06h R1 (N4): the same function the gate used — bootstrap is held only by its own questions.
  const pendingIds = new Set(
    gateBlocked.ticketKind !== undefined && isBootstrapTicketKind(gateBlocked.ticketKind)
      ? await pendingOwnNeedsDecisionQuestionIds(controlRoot, gateBlocked.ticketId)
      : await requireAnsweredQuestionIds(controlRoot, gateBlocked.ticketId),
  );
  const interview = await readInterview(controlRoot);
  if (interview === undefined || pendingIds.size === 0) return [];
  const ledger = await readDecisionLedger(controlRoot).catch(() => undefined);
  const recommended = new Map(
    (ledger?.pending ?? []).map((p) => [p.questionId, p.recommendedOptionId]),
  );
  return interview.questions
    .filter((q) => pendingIds.has(q.id))
    .flatMap((q) => linesForQuestion(q, missionId, recommended.get(q.id)));
}
